import React, { useState, useCallback, memo, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Badge } from 'react-bootstrap';

// Child component WITHOUT memo
const ChildWithoutMemo = ({ onIncrement, label }) => {
  const renderCount = React.useRef(0);
  renderCount.current += 1;
  
  console.log(`🔴 Child WITHOUT memo (${label}) rendered ${renderCount.current} times`);
  
  return (
    <Card className="mb-3">
      <Card.Header className="bg-danger text-white">
        Child Without Memo <Badge bg="warning">Renders: {renderCount.current}</Badge>
      </Card.Header>
      <Card.Body>
        <Button onClick={onIncrement} variant="primary">Increment</Button>
        <p className="mt-2 mb-0">This child re-renders on every parent render!</p>
      </Card.Body>
    </Card>
  );
};

// Child component WITH memo
const ChildWithMemo = memo(({ onIncrement, label }) => {
  const renderCount = React.useRef(0);
  renderCount.current += 1;
  
  console.log(`🟢 Child WITH memo (${label}) rendered ${renderCount.current} times`);
  
  return (
    <Card className="mb-3">
      <Card.Header className="bg-success text-white">
        Child With Memo <Badge bg="success">Renders: {renderCount.current}</Badge>
      </Card.Header>
      <Card.Body>
        <Button onClick={onIncrement} variant="primary">Increment</Button>
        <p className="mt-2 mb-0">This child only re-renders when props actually change!</p>
      </Card.Body>
    </Card>
  );
});

const Lesson15_UseCallback = () => {
  const [count, setCount] = useState(0);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    console.log('📖 Lesson 15: useCallback Hook - Loaded');
    console.log('🔄 Parent component rendered');
  });

  // WITHOUT useCallback - new function on every render
  const incrementWithoutCallback = () => {
    console.log('➕ Increment WITHOUT useCallback called');
    setCount(c => c + 1);
  };

  // WITH useCallback - same function reference
  const incrementWithCallback = useCallback(() => {
    console.log('➕ Increment WITH useCallback called');
    setCount(c => c + 1);
  }, []); // Empty dependency array means function never changes

  const toggleTheme = () => {
    console.log('');
    console.log('🎨 Theme toggle clicked - Parent will re-render');
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <LessonLayout 
      lessonNumber={15}
      title="useCallback Hook" 
      description="Prevent unnecessary re-renders with function memoization"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useCallback for function memoization</li>
          <li>Preventing child component re-renders</li>
          <li>Working with React.memo</li>
          <li>useCallback vs useMemo</li>
        </ul>
      </Alert>

      <Alert variant="info" className="mt-3">
        <strong>🎯 Experiment:</strong> Click "Toggle Theme" and watch the console. 
        The child WITHOUT memo will re-render, but the child WITH memo won't!
      </Alert>

      <Card className="mb-3">
        <Card.Header>Parent Component Controls</Card.Header>
        <Card.Body>
          <div className="d-flex gap-3 align-items-center">
            <div>
              <strong>Count:</strong> {count}
            </div>
            <div>
              <strong>Theme:</strong> {theme}
            </div>
            <Button onClick={toggleTheme} variant="secondary">
              Toggle Theme (triggers parent re-render)
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <h5 className="text-danger">❌ Without useCallback</h5>
          <ChildWithoutMemo 
            onIncrement={incrementWithoutCallback} 
            label="without-callback"
          />
          <Alert variant="danger">
            <strong>Problem:</strong> <code>incrementWithoutCallback</code> is a new function on every render,
            so even with <code>React.memo</code>, the child would re-render because props "changed"
          </Alert>
        </Col>

        <Col md={6}>
          <h5 className="text-success">✅ With useCallback</h5>
          <ChildWithMemo 
            onIncrement={incrementWithCallback} 
            label="with-callback"
          />
          <Alert variant="success">
            <strong>Solution:</strong> <code>incrementWithCallback</code> is memoized with <code>useCallback</code>,
            so it stays the same across renders. <code>React.memo</code> prevents unnecessary re-renders!
          </Alert>
        </Col>
      </Row>

      <Card className="mt-4">
        <Card.Header>useCallback Syntax</Card.Header>
        <Card.Body>
          <pre className="code-example">
{`// WITHOUT useCallback - new function every render
const handleClick = () => {
  doSomething();
};

// WITH useCallback - memoized function
const handleClick = useCallback(() => {
  doSomething();
}, []); // Dependencies

// With dependencies
const handleClick = useCallback(() => {
  doSomething(count);
}, [count]); // Recreates when count changes

// Usage with React.memo
const Child = memo(({ onClick }) => {
  return <button onClick={onClick}>Click</button>;
});`}
          </pre>
        </Card.Body>
      </Card>

      <h4 className="mt-4">📊 useCallback vs useMemo</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Hook</th>
                <th>Returns</th>
                <th>Use Case</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>useCallback</code></td>
                <td>Memoized function</td>
                <td>Prevent function recreation</td>
                <td><code>useCallback(() =&gt; fn(), [])</code></td>
              </tr>
              <tr>
                <td><code>useMemo</code></td>
                <td>Memoized value</td>
                <td>Expensive calculations</td>
                <td><code>useMemo(() =&gt; compute(), [])</code></td>
              </tr>
            </tbody>
          </table>
          <Alert variant="info" className="mb-0">
            <strong>Relationship:</strong> <code>useCallback(fn, deps)</code> is equivalent to <code>useMemo(() =&gt; fn, deps)</code>
          </Alert>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 When to Use useCallback:</strong>
        <ul className="mb-0">
          <li>Passing callbacks to optimized child components (using React.memo)</li>
          <li>Function is a dependency of useEffect or other hooks</li>
          <li>Preventing unnecessary re-creation of functions</li>
          <li><strong>Don't use</strong> for every function - only when it causes performance issues</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson15_UseCallback;
